import React, { useEffect, useState } from "react";
import { CheckCircle2, Circle, ExternalLink, RefreshCw, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { Row, IndexNowStat } from "../_ui";

/**
 * IndexNow — instant SEO crawl notification across Bing / Yandex / Naver / Seznam.
 * Free, no credentials. Auto-pings on publish.
 */
export default function IndexNowPanel({ site }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'submit' | 'rotate'
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchStatus() {
      try {
        const r = await api.get(`/sites/${site.id}/indexnow/status`);
        if (!cancelled) { setStatus(r.data); setLoading(false); }
      } catch (e) {
        if (!cancelled) {
          setFlash({ kind: "err", msg: e?.response?.data?.detail || "Failed to load IndexNow status" });
          setLoading(false);
        }
      }
    }
    fetchStatus();
    return () => { cancelled = true; };
  }, [site.id]);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/sites/${site.id}/indexnow/status`);
      setStatus(r.data);
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Failed to load IndexNow status" });
    } finally { setLoading(false); }
  }

  async function submitAll() {
    if (busy) return;
    setBusy("submit"); setFlash(null);
    try {
      const r = await api.post(`/sites/${site.id}/indexnow/submit-all`, { only_unpinged: true, limit: 1000 });
      setFlash({ kind: "ok", msg: `Submitted ${r.data.submitted} URLs · status ${r.data.result?.status || "?"}` });
      await load();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Submission failed" });
    } finally { setBusy(null); }
  }

  async function rotate() {
    if (busy) return;
    if (!window.confirm("Rotate the IndexNow key? The old key URL stops working immediately and all published articles will be re-pinged on the next submit.")) return;
    setBusy("rotate"); setFlash(null);
    try {
      await api.post(`/sites/${site.id}/indexnow/regenerate`);
      setFlash({ kind: "ok", msg: "Key rotated — all articles unpinged" });
      await load();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Rotate failed" });
    } finally { setBusy(null); }
  }

  if (loading) return <div className="text-xs text-zinc-500 mono pl-[200px]">Loading IndexNow status…</div>;
  if (!status) return <div className="text-xs text-rose-400 mono pl-[200px]">Failed to load IndexNow status</div>;

  const ok = status.eligible;
  const stats = status.stats || {};

  return (
    <div className="space-y-3" data-testid="indexnow-panel">
      <Row label="Status">
        <div className="flex items-center gap-2">
          {ok ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-sm bg-emerald-900/40 border border-emerald-700 text-emerald-300 mono uppercase tracking-wider" data-testid="indexnow-badge-eligible">
              <CheckCircle2 size={11}/> Eligible
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-sm bg-amber-900/30 border border-amber-700 text-amber-300 mono uppercase tracking-wider" data-testid="indexnow-badge-pending">
              <Circle size={11}/> {status.reason === "no_domain" ? "Waiting for domain" : "Pending"}
            </span>
          )}
          <span className="text-[11px] text-zinc-500 mono">
            {ok
              ? "Bing / Yandex / Naver / Seznam will be notified instantly on publish"
              : "Buy or link a custom domain in the Domain tab to enable"}
          </span>
        </div>
      </Row>

      <Row label="Verification key">
        <div className="flex flex-col gap-1">
          <div className="font-mono text-[11px] text-zinc-400 break-all" data-testid="indexnow-key">{status.key || "—"}</div>
          {status.key_file_url && (
            <a
              href={status.key_file_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-emerald-400 hover:text-emerald-300 underline mono"
              data-testid="indexnow-key-file-link"
            >
              <ExternalLink size={10}/> {status.key_file_url}
            </a>
          )}
        </div>
      </Row>

      <Row label="Stats">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <IndexNowStat label="Published" value={stats.published_articles ?? 0} testid="indexnow-stat-published"/>
          <IndexNowStat label="Pinged" value={stats.published_articles_pinged ?? 0} testid="indexnow-stat-pinged" tone={stats.published_articles_pinged === stats.published_articles ? "ok" : "warn"}/>
          <IndexNowStat label="URLs submitted" value={stats.total_urls_submitted ?? 0} testid="indexnow-stat-urls"/>
          <IndexNowStat label="Last ping" value={stats.last_ping_at ? new Date(stats.last_ping_at).toLocaleString() : "—"} testid="indexnow-stat-last"/>
        </div>
      </Row>

      {stats.last_status && (
        <Row label="Last response">
          <div className="font-mono text-[11px] text-zinc-400" data-testid="indexnow-last-status">
            <span className={stats.last_status.ok ? "text-emerald-400" : "text-rose-400"}>
              {stats.last_status.ok ? "OK" : "FAIL"}
            </span>
            {" · HTTP "}{stats.last_status.status ?? "?"}
            {stats.last_status.reason ? ` · ${stats.last_status.reason}` : ""}
            {" · batches "}{stats.last_status.batches_sent ?? 0}/{stats.last_status.total_batches ?? "?"}
          </div>
        </Row>
      )}

      <Row label="Actions">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={submitAll}
            disabled={!ok || busy}
            data-testid="indexnow-submit-all-btn"
            className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-sm text-xs font-semibold mono"
          >
            <Rocket size={12}/> {busy === "submit" ? "Submitting…" : "Submit unpinged articles"}
          </button>
          <button
            onClick={rotate}
            disabled={busy}
            data-testid="indexnow-rotate-btn"
            className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-amber-500 hover:text-amber-300 text-zinc-300 px-3 py-1.5 rounded-sm text-xs mono disabled:opacity-40"
          >
            <RefreshCw size={12}/> {busy === "rotate" ? "Rotating…" : "Rotate key"}
          </button>
        </div>
      </Row>

      {flash && (
        <div className={`text-[11px] mono pl-[200px] ${flash.kind === "ok" ? "text-emerald-400" : "text-rose-400"}`} data-testid="indexnow-flash">
          {flash.msg}
        </div>
      )}

      <div className="text-[11px] text-zinc-500 mono pl-[200px]">
        IndexNow is a free, no-credentials protocol supported by Bing, Yandex, Naver, Seznam, and (experimentally) Google. Every publish + bulk-publish + auto-publish auto-pings.
      </div>
    </div>
  );
}
